import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'
import { T } from '@/lib/design'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

type Props = {
  compact?: boolean
  className?: string
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true
}

export function PwaInstallButton({ compact = false, className }: Props) {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(false)

  useEffect(() => {
    setInstalled(isStandalone())

    const nav = window.navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<Array<{ platform?: string; id?: string }>>
    }

    nav.getInstalledRelatedApps?.()
      .then((apps) => {
        if (apps.length > 0) setInstalled(true)
      })
      .catch(() => undefined)

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setPromptEvent(event as BeforeInstallPromptEvent)
    }

    const onAppInstalled = () => {
      setInstalled(true)
      setPromptEvent(null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  if (installed || !promptEvent) return null

  const install = async () => {
    await promptEvent.prompt()
    const choice = await promptEvent.userChoice
    if (choice.outcome === 'accepted') {
      setInstalled(true)
    }
    setPromptEvent(null)
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className={className}
        onClick={install}
        style={{
          minHeight: compact ? 34 : 38,
          padding: compact ? '0 10px' : '0 14px',
          border: `1px solid ${T.pepperBorder}`,
          borderRadius: 9,
          background: promptEvent ? T.pepper : T.pepperBg,
          color: promptEvent ? '#fff' : T.pepper,
          fontFamily: T.sans,
          fontSize: compact ? 12 : 13,
          fontWeight: 800,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
          whiteSpace: 'nowrap',
        }}
      >
        <Download size={compact ? 14 : 15} />
        {compact ? 'Install' : 'Install app'}
      </button>
    </span>
  )
}
